import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../constants/formStudioConfig';
import { isStaticBuilderField, isSubStatusField, isStatusField } from '../utils/formStudioFormUtils';

export default function FormReadinessSummary({
  studio,
  hasFormId,
  setupProgress,
  onGoToFields,
  onGoToAutomations,
}) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.readiness;
  const form = studio.form || {};
  const fields = form.leads_form_items || [];

  const summary = useMemo(() => {
    const blockers = [];
    let showGoToFields = false;

    if (!String(form.leads_form_name || '').trim()) {
      blockers.push('Form name is required before publishing.');
    }
    if (!hasFormId) {
      blockers.push('Create the form to unlock automations and assets.');
    }

    const editableFields = fields.filter((field) => !isStaticBuilderField(field));
    if (hasFormId && !editableFields.length) {
      blockers.push('Add at least one custom field.');
      showGoToFields = true;
    } else if (!hasFormId && !editableFields.length) {
      blockers.push('Add at least one custom field after creating the form.');
    }

    const hasStatus = fields.some(isStatusField);
    const hasSubStatus = fields.some(isSubStatusField);
    if (hasSubStatus && !hasStatus) {
      blockers.push('Sub-status fields exist without a parent status field.');
    }
    if (studio.isDirty) {
      blockers.push('You have unsaved changes.');
    }

    const nextSteps = [];
    if (blockers.length === 0 && hasFormId) {
      if (hasStatus && !setupProgress?.statusConfigured) {
        nextSteps.push(copy.configureStatusOptions);
      } else if (!hasStatus) {
        nextSteps.push(copy.statusNotRequired);
      }

      if (!setupProgress?.automationsConfigured) {
        nextSteps.push(copy.configureAutomations);
      }
    }

    const coreReady = blockers.length === 0;
    const fullyReady = coreReady && (!hasStatus || setupProgress?.statusConfigured);

    return {
      blockers,
      nextSteps,
      showGoToFields,
      coreReady,
      fullyReady,
      showGoToFieldsForStatus: nextSteps.includes(copy.configureStatusOptions),
      showGoToAutomations: nextSteps.includes(copy.configureAutomations),
    };
  }, [
    copy.configureAutomations,
    copy.configureStatusOptions,
    copy.statusNotRequired,
    fields,
    form.leads_form_name,
    hasFormId,
    setupProgress?.automationsConfigured,
    setupProgress?.statusConfigured,
    studio.isDirty,
  ]);

  if (summary.fullyReady) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: radiusPx.md,
          bgcolor: semantic.states.successSoft,
          color: semantic.states.success,
        }}
      >
        <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
          {copy.allCompleteTitle}
        </Typography>
      </Box>
    );
  }

  if (summary.coreReady) {
    return (
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: radiusPx.md,
          border: `1px solid ${semantic.interactive.primaryBorder ?? semantic.interactive.primary}`,
          bgcolor: semantic.interactive.primarySoft,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Box>
            <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
              {copy.coreCompleteTitle}
            </Typography>
            <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary, mt: 0.5 }}>
              {copy.coreCompleteDescription}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {summary.showGoToFieldsForStatus && onGoToFields ? (
              <Button variant="outline" onClick={onGoToFields}>
                Go to fields
              </Button>
            ) : null}
            {summary.showGoToAutomations && onGoToAutomations ? (
              <Button variant="outline" onClick={onGoToAutomations}>
                Go to automations
              </Button>
            ) : null}
          </Stack>
        </Stack>
        <Stack spacing={0.5}>
          {summary.nextSteps.map((step) => (
            <Typography key={step} sx={{ ...typographyStyles.caption }}>
              • {step}
            </Typography>
          ))}
        </Stack>
      </Box>
    );
  }

  if (!summary.blockers.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.states.warning}`,
        bgcolor: semantic.states.warningSoft,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
          Readiness checks
        </Typography>
        {summary.showGoToFields && onGoToFields ? (
          <Button variant="outline" onClick={onGoToFields}>
            Go to fields
          </Button>
        ) : null}
      </Stack>
      <Stack spacing={0.5}>
        {summary.blockers.map((warning) => (
          <Typography key={warning} sx={{ ...typographyStyles.caption }}>
            • {warning}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}
